// Autosave storage (pacing spec §4 A3): a ring of AUTOSAVE_SLOTS saves in
// IndexedDB, falling back to localStorage when IndexedDB is unavailable
// (private windows, some embedded webviews). Every call is best-effort: it
// resolves with null / false instead of throwing, so a storage failure can
// never interrupt play.

export const AUTOSAVE_SLOTS = 3;
const DB_NAME = 'aod-saves';
const STORE = 'autosaves';
const LS_PREFIX = 'aod:autosave:';

export interface AutosaveEntry {
  /** Ring slot, 0..AUTOSAVE_SLOTS-1. */
  readonly slot: number;
  /** Epoch ms when written. */
  readonly savedAt: number;
  /** Sim tick at save time. */
  readonly tick: number;
  /** Human label for the menu, e.g. "Year 2 · Day 14". */
  readonly label: string;
  /** Full save JSON from sim.saveGame(). */
  readonly json: string;
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE, { keyPath: 'slot' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function idbAll(db: IDBDatabase): Promise<AutosaveEntry[] | null> {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result as AutosaveEntry[]);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

function idbPut(db: IDBDatabase, entry: AutosaveEntry): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(entry);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

function lsAll(): AutosaveEntry[] {
  const out: AutosaveEntry[] = [];
  try {
    for (let slot = 0; slot < AUTOSAVE_SLOTS; slot++) {
      const raw = localStorage.getItem(`${LS_PREFIX}${slot}`);
      if (raw) out.push(JSON.parse(raw) as AutosaveEntry);
    }
  } catch {
    /* unreadable storage: treat as empty */
  }
  return out;
}

/** All autosaves, newest first. */
export async function listAutosaves(): Promise<AutosaveEntry[]> {
  const db = await openDb();
  const all = (db ? await idbAll(db) : null) ?? lsAll();
  db?.close();
  return all.filter((e) => e && typeof e.json === 'string').sort((a, b) => b.savedAt - a.savedAt);
}

export async function latestAutosave(): Promise<AutosaveEntry | null> {
  return (await listAutosaves())[0] ?? null;
}

/** The slot the next save overwrites: an empty one, else the oldest. */
export function nextSlot(existing: ReadonlyArray<AutosaveEntry>): number {
  for (let slot = 0; slot < AUTOSAVE_SLOTS; slot++) {
    if (!existing.some((e) => e.slot === slot)) return slot;
  }
  return [...existing].sort((a, b) => a.savedAt - b.savedAt)[0]?.slot ?? 0;
}

/** Write a save into the ring. Resolves false (never throws) on failure. */
export async function writeAutosave(save: Omit<AutosaveEntry, 'slot'>): Promise<boolean> {
  const db = await openDb();
  if (db) {
    const existing = (await idbAll(db)) ?? [];
    const ok = await idbPut(db, { ...save, slot: nextSlot(existing) });
    db.close();
    if (ok) return true;
  }
  try {
    const entry = { ...save, slot: nextSlot(lsAll()) };
    localStorage.setItem(`${LS_PREFIX}${entry.slot}`, JSON.stringify(entry));
    return true;
  } catch {
    return false; // quota exceeded / storage disabled
  }
}
